import { useCallback, useEffect, useRef } from 'react';
import getCurrentTime from '../timeFactory';

export interface EmailViewTime {
  getViewMs: (contactId: string) => number;
  isRunning: (contactId: string) => boolean;
}

/**
 * Accumulates, per email contact, how long that contact was open on screen (the one the agent was viewing).
 * Pass `null` when no email is being viewed (agent is on another channel, or otherwise away);
 * the previously viewed contact stops accumulating until it is passed again.
 * Time lives in refs: readers poll `getViewMs`, so no re-render happens on every tick.
 */
export function useEmailViewTime(viewedContactId: string | null): EmailViewTime {
  const accumulatedMs = useRef<Record<string, number>>({});
  const viewing = useRef<{ contactId: string; startedAt: number } | null>(null);

  useEffect(() => {
    const now = getCurrentTime().getTime();

    if (viewing.current) {
      const { contactId, startedAt } = viewing.current;
      accumulatedMs.current[contactId] = (accumulatedMs.current[contactId] ?? 0) + (now - startedAt);
    }

    viewing.current = viewedContactId ? { contactId: viewedContactId, startedAt: now } : null;
  }, [viewedContactId]);

  const getViewMs = useCallback((contactId: string): number => {
    const base = accumulatedMs.current[contactId] ?? 0;

    if (viewing.current?.contactId !== contactId) {
      return base;
    }

    return base + (getCurrentTime().getTime() - viewing.current.startedAt);
  }, []);

  return { getViewMs, isRunning: contactId => viewedContactId === contactId };
}
